export type PageSize = "fit" | "a4" | "letter";

export type PdfImage = {
  /** JPEG-encoded image bytes (embedded as-is with DCTDecode). */
  jpeg: Uint8Array;
  /** Pixel dimensions of the image. */
  width: number;
  height: number;
};

export type PdfOptions = {
  pageSize: PageSize;
  /** Margin in PDF points (1/72 in). */
  margin: number;
};

const PAGE_SIZES: Record<Exclude<PageSize, "fit">, [number, number]> = {
  a4: [595.28, 841.89],
  letter: [612, 792],
};

// Images are treated as 96 dpi when the page is sized to fit them.
const PX_TO_PT = 0.75;

const fmt = (n: number) => Number(n.toFixed(2)).toString();

type Placement = {
  pageWidth: number;
  pageHeight: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

export function layoutPage(image: PdfImage, options: PdfOptions): Placement {
  const { margin } = options;

  if (options.pageSize === "fit") {
    const width = image.width * PX_TO_PT;
    const height = image.height * PX_TO_PT;
    return {
      pageWidth: width + margin * 2,
      pageHeight: height + margin * 2,
      x: margin,
      y: margin,
      width,
      height,
    };
  }

  let [pageWidth, pageHeight] = PAGE_SIZES[options.pageSize];
  // Pick the orientation that suits the image.
  if (image.width > image.height) {
    [pageWidth, pageHeight] = [pageHeight, pageWidth];
  }
  const availWidth = Math.max(pageWidth - margin * 2, 1);
  const availHeight = Math.max(pageHeight - margin * 2, 1);
  const scale = Math.min(availWidth / image.width, availHeight / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  return {
    pageWidth,
    pageHeight,
    x: (pageWidth - width) / 2,
    y: (pageHeight - height) / 2,
    width,
    height,
  };
}

/** Builds a PDF file with one JPEG image per page. */
export function buildPdf(images: PdfImage[], options: PdfOptions): Uint8Array {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;

  const push = (chunk: Uint8Array) => {
    chunks.push(chunk);
    length += chunk.length;
  };
  const pushText = (text: string) => push(encoder.encode(text));
  const startObject = (id: number) => {
    offsets[id] = length;
    pushText(`${id} 0 obj\n`);
  };

  pushText("%PDF-1.4\n");
  // Binary marker so transfer tools treat the file as binary.
  push(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));

  // Object ids: 1 catalog, 2 pages, then 3 per image (page, content, image).
  const pageId = (i: number) => 3 + i * 3;
  const kids = images.map((_, i) => `${pageId(i)} 0 R`).join(" ");

  startObject(1);
  pushText("<< /Type /Catalog /Pages 2 0 R >>\nendobj\n");
  startObject(2);
  pushText(`<< /Type /Pages /Kids [${kids}] /Count ${images.length} >>\nendobj\n`);

  images.forEach((image, i) => {
    const place = layoutPage(image, options);
    const page = pageId(i);
    const content = `q ${fmt(place.width)} 0 0 ${fmt(place.height)} ${fmt(place.x)} ${fmt(place.y)} cm /Im0 Do Q`;

    startObject(page);
    pushText(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${fmt(place.pageWidth)} ${fmt(place.pageHeight)}] ` +
        `/Resources << /XObject << /Im0 ${page + 2} 0 R >> >> /Contents ${page + 1} 0 R >>\nendobj\n`,
    );

    startObject(page + 1);
    pushText(`<< /Length ${content.length} >>\nstream\n${content}\nendstream\nendobj\n`);

    startObject(page + 2);
    pushText(
      `<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} ` +
        `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${image.jpeg.length} >>\nstream\n`,
    );
    push(image.jpeg);
    pushText("\nendstream\nendobj\n");
  });

  const objectCount = 3 + images.length * 3;
  const xrefOffset = length;
  let xref = `xref\n0 ${objectCount}\n0000000000 65535 f \n`;
  for (let id = 1; id < objectCount; id++) {
    xref += `${String(offsets[id]).padStart(10, "0")} 00000 n \n`;
  }
  pushText(xref);
  pushText(
    `trailer\n<< /Size ${objectCount} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`,
  );

  const out = new Uint8Array(length);
  let position = 0;
  for (const chunk of chunks) {
    out.set(chunk, position);
    position += chunk.length;
  }
  return out;
}
